import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, ArrowRight, AlertTriangle } from "lucide-react";
import { journeyApi } from "@/lib/journeyApi";
import { JourneyTopBar, JourneyFooter } from "@/components/JourneyChrome";

const MAX_TRIES = 15;

export default function PurchaseSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | failed
  const [tries, setTries] = useState(0);

  const poll = useCallback(async () => {
    if (!sessionId) { setStatus("failed"); return; }
    try {
      const data = await journeyApi.paymentStatus(sessionId);
      if (data.payment_status === "paid") { setStatus("paid"); return; }
      if (["failed", "expired"].includes(data.payment_status)) { setStatus("failed"); return; }
      setTries((t) => t + 1);
    } catch {
      setTries((t) => t + 1);
    }
  }, [sessionId]);

  useEffect(() => {
    if (status !== "checking") return;
    if (tries >= MAX_TRIES) { setStatus("failed"); return; }
    const id = setTimeout(poll, tries === 0 ? 300 : 2000);
    return () => clearTimeout(id);
  }, [poll, tries, status]);

  return (
    <div className="flex min-h-screen flex-col bg-[#FAF8F4] text-[#171717]" data-testid="purchase-success-page">
      <JourneyTopBar showSignIn={false} />
      <main className="flex flex-1 items-center justify-center px-5 py-16">
        <div className="w-full max-w-lg text-center">
          {status === "checking" && (
            <div data-testid="success-checking">
              <div className="mx-auto min-h-10 w-10 animate-spin rounded-full border-2 border-[#E5E0D9] border-t-[#2F6B57]" />
              <p className="mt-6 text-lg text-[#525252]">Confirming your payment…</p>
            </div>
          )}

          {status === "paid" && (
            <div className="scale-in rounded-3xl pe-surface p-9 shadow-[0_30px_60px_-30px_rgba(23,23,23,0.2)]" data-testid="success-paid">
              <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#DDE7DF] text-[#164C3B]">
                <CheckCircle2 className="h-8 w-8" strokeWidth={2.2} />
              </span>
              <p className="mt-6 text-xs font-semibold uppercase tracking-[0.16em] text-[#C9A86A]">Welcome to The Paycheck Edit™</p>
              <h1 className="mt-3 font-serif text-3xl leading-tight text-[#171717] sm:text-4xl">Your purchase is complete.</h1>
              <p className="mt-4 text-base leading-relaxed text-[#525252]">
                Now let's turn that purchase into your first Edit.
              </p>
              <button
                type="button"
                onClick={() => navigate(`/activate?session_id=${encodeURIComponent(sessionId)}`)}
                data-testid="activate-cta"
                className="group mt-8 inline-flex min-h-[54px] w-full items-center justify-center gap-2.5 rounded-full bg-[#2F6B57] px-8 text-base font-semibold text-white transition-all duration-200 hover:bg-[#255544]"
              >
                Activate My The Paycheck Edit
                <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-0.5" />
              </button>
            </div>
          )}

          {status === "failed" && (
            <div className="rounded-3xl pe-surface p-9" data-testid="success-failed">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#F3E4DE] text-[#8B3A2B]">
                <AlertTriangle className="h-7 w-7" />
              </span>
              <h1 className="mt-6 font-serif text-2xl text-[#171717]">We couldn't confirm your payment</h1>
              <p className="mt-3 text-[15px] leading-relaxed text-[#525252]">
                If you were charged, give it a moment and refresh. Otherwise you can start again. You are never granted
                access without a completed purchase.
              </p>
              <Link
                to="/"
                data-testid="back-to-sales-link"
                className="mt-7 inline-flex min-h-[48px] items-center justify-center rounded-full border border-[#2F6B57] px-7 text-sm font-semibold text-[#2F6B57] transition-colors hover:bg-[#DDE7DF]"
              >
                Back to The Paycheck Edit
              </Link>
            </div>
          )}
        </div>
      </main>
      <JourneyFooter />
    </div>
  );
}
